import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import ColourwayCreateRow, { BLANK_COLOURWAY } from "./ColourwayCreateRow";
import ColourwayRow from "./ColourwayRow";

/**
 * ColourwaysManager — full CRUD for the colourway palette + per-colourway stock.
 * Stock count is the scarcity lever ("Only X left" badges go live when ≤5).
 *
 * Owns the table-level state (rows, dirty flags, image previews) and delegates
 * row rendering to <ColourwayRow /> and the inline create form to
 * <ColourwayCreateRow />.
 */
const ColourwaysManager = ({ authClient, products = [] }) => {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(BLANK_COLOURWAY);
  const [savingId, setSavingId] = useState(null);
  // Track which colourway has an active file picker + the latest uploaded
  // image preview (keyed by colourway slug) so the admin gets instant feedback.
  const [uploadingId, setUploadingId] = useState(null);
  const [previewBySlug, setPreviewBySlug] = useState({});
  const fileInputs = useRef({});

  const reload = async () => {
    try {
      const [{ data: list }, { data: imgs }] = await Promise.all([
        authClient().get("/admin/cms/colourways"),
        authClient().get("/admin/product-images"),
      ]);
      setItems(list);
      // Build slug → latest non-deleted hero image URL (no grip).
      const latest = {};
      imgs.forEach((i) => {
        if (i.is_deleted || i.grip_slug) return;
        if (!latest[i.colourway_slug]) latest[i.colourway_slug] = i.url;
      });
      setPreviewBySlug(latest);
    } catch {
      toast.error("Couldn't load colourways");
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFieldChange = (idx, key, val) => {
    setItems((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, [key]: val, _dirty: true } : c))
    );
  };

  const onSave = async (cw) => {
    setSavingId(cw.id);
    try {
      const payload = {
        product_slug: cw.product_slug,
        slug: cw.slug,
        name: cw.name,
        hex: cw.hex,
        description: cw.description || "",
        season: cw.season || "FW26",
        stock: Number(cw.stock) || 0,
        featured_this_season: Boolean(cw.featured_this_season),
        is_active: Boolean(cw.is_active),
        sort_order: Number(cw.sort_order) || 0,
      };
      await authClient().put(`/admin/cms/colourways/${cw.id}`, payload);
      toast.success(`${cw.name} updated`);
      reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Update failed");
    } finally {
      setSavingId(null);
    }
  };

  const onDelete = async (cw) => {
    if (!window.confirm(`Delete ${cw.name}? This permanently removes it.`)) return;
    try {
      await authClient().delete(`/admin/cms/colourways/${cw.id}`);
      toast.success("Colourway removed");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  const onCreate = async () => {
    if (!draft.slug || !draft.name) {
      toast.error("Slug and name are required");
      return;
    }
    try {
      await authClient().post("/admin/cms/colourways", {
        ...draft,
        stock: Number(draft.stock) || 0,
        sort_order: Number(draft.sort_order) || 0,
      });
      toast.success(`${draft.name} created`);
      setCreating(false);
      setDraft(BLANK_COLOURWAY);
      reload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Create failed");
    }
  };

  const onPickFile = (cw) => {
    const ref = fileInputs.current[cw.slug];
    if (ref) ref.click();
  };

  const onUpload = async (cw, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingId(cw.id);
    try {
      const fd = new FormData();
      fd.append("colourway_slug", cw.slug);
      fd.append("file", file);
      const { data } = await authClient().post("/admin/product-images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreviewBySlug((p) => ({ ...p, [cw.slug]: data.url }));
      toast.success(`Hero image updated for ${cw.name}`);
      e.target.value = "";
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploadingId(null);
    }
  };

  const registerFileInput = (slug, el) => {
    fileInputs.current[slug] = el;
  };

  return (
    <div data-testid="colourways-manager">
      <div className="flex items-center justify-between mb-4">
        <p className="overline">Colourways ({items.length})</p>
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          className="btn-secondary !px-4 !py-2 text-xs"
          data-testid="cw-add-button"
        >
          <Plus size={14} className="mr-1.5" /> {creating ? "Cancel" : "New colourway"}
        </button>
      </div>

      {creating && (
        <ColourwayCreateRow
          draft={draft}
          setDraft={setDraft}
          products={products}
          onCreate={onCreate}
        />
      )}

      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-xs min-w-[900px]">
          <thead className="border-b border-line">
            <tr className="text-left">
              <th className="px-3 py-3 overline">Swatch · Image</th>
              <th className="px-3 py-3 overline">Name / Slug</th>
              <th className="px-3 py-3 overline">Hex</th>
              <th className="px-3 py-3 overline">Season</th>
              <th className="px-3 py-3 overline">Stock</th>
              <th className="px-3 py-3 overline">Featured</th>
              <th className="px-3 py-3 overline">Active</th>
              <th className="px-3 py-3 overline">Sort</th>
              <th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {items.map((cw, idx) => (
              <ColourwayRow
                key={cw.id}
                cw={cw}
                idx={idx}
                preview={previewBySlug[cw.slug]}
                uploading={uploadingId === cw.id}
                saving={savingId === cw.id}
                onFieldChange={onFieldChange}
                onSave={onSave}
                onDelete={onDelete}
                onPickFile={onPickFile}
                onUpload={onUpload}
                registerFileInput={registerFileInput}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ColourwaysManager;
